import React, { useEffect, useState } from 'react'
import { SimpleSection } from '@/Components/custom/simple-section'
import useLocalizedValue from '@/hooks/use-localized-value'
import { Block } from '@/hooks/use-block-renderer'
import RCarousel from '@/Components/custom/carousel'
import QuickAccessLinks from './sections/QuickAccessLinks'
import TelecomPlansGrid1 from '@/Components/custom/dynamic-island'
import { InteractiveHeroImage } from './custom/interactive-hero-image'
import SupportSection from './custom/support-section'
import { SplitSection } from './custom/split-section'
import NewsSection from './sections/news-section'
import ServiceSection from './sections/service-section'
import TariffSection from './custom/tariff-section'
import { BundleTab, Campaign } from '@/types'
import TabbedSection from './custom/tabbed-section'
import RichContentSection from './custom/rich-content-section'
import FAQSection from '@/Components/sections/faqs-section'
import { cn, convertComponentAsClassName } from '@/lib/utils'
import { Head } from '@inertiajs/react'
import CampaignSection from '@/Components/sections/campaign-section'
import QuickNavList from './sections/quick-nav-list'

interface BlockComponentProps {
  data: Block['data']
  component?: null | string
}

const CarouselBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <RCarousel
    images={data.media}
    className="w-full"
    imageClass="object-fit w-full aspect-[4/2] h-auto max-h-[450px] rounded-2xl md:rounded-[28px] lg:rounded-[40px]"
  />
)

const HeroBlock: React.FC<BlockComponentProps> = ({ data }) => {
  const title = useLocalizedValue(data.title || {})
  const subtitle = useLocalizedValue(data.subtitle || {})
  return (
    <>
      {data.media && (
        <Head>
          <link rel="preload" href={data.media} as="image" />
        </Head>
      )}
      <SimpleSection top={data.media_order === 1} title={title} subtitle={subtitle}>
        <img
          src={data.media || '/images/placeholder.svg'}
          alt={title || ''}
          className={cn(
            'h-full max-h-[450px] w-full rounded-2xl md:rounded-[28px] lg:rounded-[40px]',
            data.media.includes('placeholder.svg') ? 'object-cover' : 'object-contain'
          )}
          loading="eager"
        />
      </SimpleSection>
    </>
  )
}

const QuickAccessLinksBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <QuickNavList items={data.links} />
)

const CampaignBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <CampaignSection content={data as Campaign} />
)

const BundleSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <TelecomPlansGrid1 bundles={data.bundles} title={data.title} subtitle={data.subtitle} />
)

const SupportSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <SupportSection
    items={data?.support || []}
    title={useLocalizedValue(data.title || {}) || ''}
    subtitle={useLocalizedValue(data.subtitle || {}) || ''}
  />
)

const SplitSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <SplitSection items={data.items} title={data.title} subtitle={data.subtitle} media={data.media} />
)

const InteractiveHeroImageBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <InteractiveHeroImage
    sentences={data.sentences.map((item: any) => {
      return useLocalizedValue(item.sentence)
    })}
    image={data.media}
  />
)

const NewsSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <NewsSection title={data.title} subtitle={data.subtitle} articles={data.articles} />
)

const ServiceSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <ServiceSection title={data.title} subtitle={data.subtitle} services={data.services} />
)

const TariffSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <TariffSection tabs={data.tariffs as BundleTab[]} />
)

const PlansSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <TariffSection tabs={data.plans} isSimple={data.is_simple} />
)

const TabbedSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <TabbedSection tabs={data.tabs} />
)

const RichContentSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <RichContentSection content={data.richContent} className={data.title ? '' : 'px-0'} />
)

const FaqsSectionBlock: React.FC<BlockComponentProps> = ({ data }) => (
  <FAQSection questions={data.questions} icon={data.icon} adjustGap={data.title ? false : true} />
)

const MultiOptionSection: React.FC<BlockComponentProps> = ({ data, component }) => (
  <TabbedSection tabs={data.tabs} className={component} />
)

const blockComponents: { [key: string]: React.FC<BlockComponentProps> } = {
  hero_image: HeroBlock,
  carousel: CarouselBlock,
  quick_access_links: QuickAccessLinksBlock,
  bundle_section: BundleSectionBlock,
  hero_image_interactive: InteractiveHeroImageBlock,
  support_section: SupportSectionBlock,
  split_section: SplitSectionBlock,
  campaign: CampaignBlock,
  news_section: NewsSectionBlock,
  service_section: ServiceSectionBlock,
  tariff_section: TariffSectionBlock,
  plans_section: PlansSectionBlock,
  tabbed_section: TabbedSectionBlock,
  rich_content_section: RichContentSectionBlock,
  multi_option_section: MultiOptionSection,
  faqs_section: FaqsSectionBlock,
}

interface BlockRendererProps {
  blocks: Block[]
  customComponents?: { [key: string]: React.FC<BlockComponentProps> }
}

export const BlockRenderer: React.FC<BlockRendererProps> = ({ blocks, customComponents = {} }) => {
  const allComponents = { ...blockComponents, ...customComponents }

  return (
    <>
      {blocks.map((block, index) => {
        // Skip rendering if block is not published, except when is_published is undefined (backward compatibility)
        if (!block.data || block.data.is_published === 0 || block.data.is_published === false) {
          return null
        }

        const BlockComponent = allComponents[block.type]
        return BlockComponent ? (
          <BlockComponent
            key={index}
            data={block.data}
            component={convertComponentAsClassName(block.component)}
          />
        ) : null
      })}
    </>
  )
}
